import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { FolderPlus, Folder, Loader2, Trash2, ArrowLeft, FileText, FileSpreadsheet, ExternalLink, X as XIcon, Lock, ListChecks } from "lucide-react";

function downloadBlob(data, filename) {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function Lists() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPaid = user?.is_paid;
  const [lists, setLists] = useState(null);
  const [active, setActive] = useState(null);
  const [loadingActive, setLoadingActive] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [exporting, setExporting] = useState("");

  const loadLists = () => api.get("/lists").then(({ data }) => setLists(data.lists)).catch(() => setLists([]));
  useEffect(() => { loadLists(); }, []);

  const openList = async (id) => {
    setLoadingActive(true);
    try { const { data } = await api.get(`/lists/${id}`); setActive(data); }
    catch { toast.error("Could not open list"); } finally { setLoadingActive(false); }
  };

  const createList = async () => {
    const name = newName.trim();
    if (!name) return;
    try { await api.post("/lists", { name }); setNewName(""); setCreating(false); loadLists(); toast.success("List created"); }
    catch { toast.error("Could not create list"); }
  };

  const deleteList = async (id) => {
    try { await api.delete(`/lists/${id}`); setLists((l) => l.filter((x) => x.id !== id)); if (active?.id === id) setActive(null); toast.success("List deleted"); }
    catch { toast.error("Could not delete list"); }
  };

  const removeItem = async (contactId) => {
    try { await api.delete(`/lists/${active.id}/items/${contactId}`); setActive((a) => ({ ...a, contacts: a.contacts.filter((c) => c.id !== contactId), count: a.count - 1 })); }
    catch { toast.error("Could not remove"); }
  };

  const exportList = async (fmt) => {
    if (!isPaid) { toast.error("Downloads are a Pro feature."); navigate("/billing"); return; }
    setExporting(fmt);
    try {
      const items = active.contacts.map((c) => ({ id: c.id, reason: "", match: "Medium" }));
      const { data } = await api.post(`/export/${fmt}`, { items, query: `List: ${active.name}`, include_reasoning: false, scope: "filtered" }, { responseType: "blob" });
      downloadBlob(data, fmt === "pdf" ? `${active.name}.pdf` : `${active.name}.xlsx`);
      toast.success(`${fmt.toUpperCase()} exported`);
    } catch { toast.error("Export failed"); } finally { setExporting(""); }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto">
        {!active ? (
          <>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
              <div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Lead Lists</h1>
                <p className="text-slate-400 mt-1">Save the best matches from any search into named lists you can revisit and export.</p>
              </div>
              <button onClick={() => setCreating(true)} data-testid="create-list-button" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"><FolderPlus className="w-4 h-4" /> New list</button>
            </div>

            {creating && (
              <div className="flex items-center gap-2 mb-6 rounded-xl border border-blue-500/30 bg-[#111726] p-3" data-testid="create-list-form">
                <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createList()} data-testid="new-list-name-input" placeholder="e.g. Seed Investors, Warm Intros…" className="flex-1 bg-[#0D121C] border border-[#27334D] rounded-md px-3 py-2 text-sm outline-none focus:border-blue-500" />
                <button onClick={createList} data-testid="new-list-save" className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md text-sm font-medium">Create</button>
                <button onClick={() => { setCreating(false); setNewName(""); }} className="p-2 text-slate-500 hover:text-slate-300"><XIcon className="w-4 h-4" /></button>
              </div>
            )}

            {lists === null ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm py-12 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading lists…</div>
            ) : lists.length === 0 ? (
              <div className="text-center py-16 rounded-2xl border border-dashed border-[#27334D] bg-[#111726]">
                <ListChecks className="w-7 h-7 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No lists yet.</p>
                <p className="text-slate-600 text-sm mt-1">Run a search, tick the best matches, and add them to a list.</p>
                <button onClick={() => navigate("/search")} className="mt-4 text-sm text-blue-400 hover:text-blue-300">Go to Ask Network →</button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {lists.map((l) => (
                  <div key={l.id} data-testid={`list-card-${l.id}`} className="group rounded-2xl border border-[#27334D] bg-[#111726] p-5 hover:border-blue-500/40 transition-colors">
                    <div className="flex items-start justify-between">
                      <button onClick={() => openList(l.id)} data-testid={`list-open-${l.id}`} className="flex items-center gap-3 text-left flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-blue-600/15 border border-blue-500/25 flex items-center justify-center shrink-0"><Folder className="w-5 h-5 text-blue-400" /></div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{l.name}</div>
                          <div className="text-xs text-slate-500 font-mono-niq">{l.count} contact{l.count !== 1 ? "s" : ""}</div>
                        </div>
                      </button>
                      <button onClick={() => deleteList(l.id)} data-testid={`list-delete-${l.id}`} className="p-1.5 rounded-md text-slate-600 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <button onClick={() => { setActive(null); loadLists(); }} data-testid="list-back-button" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-5"><ArrowLeft className="w-4 h-4" /> All lists</button>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
              <div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="active-list-name">{active.name}</h1>
                <p className="text-slate-500 text-sm font-mono-niq mt-1">{active.count} contact{active.count !== 1 ? "s" : ""}</p>
              </div>
              {active.count > 0 && (
                <div className="flex gap-2">
                  <button onClick={() => exportList("pdf")} disabled={!!exporting} data-testid="list-export-pdf" className="flex items-center gap-1.5 text-xs border border-[#27334D] hover:border-slate-500 px-3 py-2 rounded-lg transition-colors">{!isPaid ? <Lock className="w-3.5 h-3.5 text-slate-500" /> : exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 text-red-400" />} PDF</button>
                  <button onClick={() => exportList("excel")} disabled={!!exporting} data-testid="list-export-excel" className="flex items-center gap-1.5 text-xs border border-[#27334D] hover:border-slate-500 px-3 py-2 rounded-lg transition-colors">{!isPaid ? <Lock className="w-3.5 h-3.5 text-slate-500" /> : exporting === "excel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />} Excel</button>
                </div>
              )}
            </div>

            {loadingActive ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm py-12 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : active.contacts.length === 0 ? (
              <div className="text-center py-16 text-slate-500 rounded-2xl border border-[#1E2A3F] bg-[#111726]">This list is empty. Add matches from a search.</div>
            ) : (
              <div className="space-y-3">
                {active.contacts.map((c) => (
                  <div key={c.id} data-testid={`list-item-${c.id}`} className="rounded-xl border border-[#27334D] bg-[#111726] p-4 flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-blue-600/15 border border-blue-500/25 flex items-center justify-center text-sm font-semibold text-blue-300 shrink-0">{(c.first_name || "?")[0]}</div>
                      <div className="min-w-0">
                        {c.profile_url ? (
                          <a href={c.profile_url} target="_blank" rel="noopener noreferrer" className="font-medium inline-flex items-center gap-1.5 text-slate-100 hover:text-blue-400 transition-colors group">{c.first_name} {c.last_name}<ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" /></a>
                        ) : (<div className="font-medium truncate">{c.first_name} {c.last_name}</div>)}
                        <div className="text-xs text-slate-500 truncate">{c.position} · {c.normalized_company || c.company}</div>
                        {c.email && <a href={`mailto:${c.email}`} className="text-xs text-slate-400 hover:text-blue-400 transition-colors">{c.email}</a>}
                        <div className="flex flex-wrap gap-2 mt-1.5 text-[11px] text-slate-500 font-mono-niq">
                          {c.sector && <span>{c.sector}</span>}{c.seniority && <span>· {c.seniority}</span>}{c.region && <span>· {c.region}</span>}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => removeItem(c.id)} data-testid={`list-item-remove-${c.id}`} className="p-1.5 rounded-md text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0"><XIcon className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
